import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import {
  CatalogType,
  AssetClass,
  AssetCriticalityFactor,
  AssetStatusCatalog,
  DocumentType,
  ProblemCode,
  CauseCode,
  RemedyCode,
  WorkOrderPriority,
  WorkOrderTypeCatalog,
  LaborType,
  Trade,
  PartCategory,
  UnitOfMeasure,
  StorageLocationType,
  TransactionReason,
  ShiftType,
  SchedulePriority,
  ConstraintType,
  Skill,
  SkillLevel,
  CertificationType,
  PermitType,
  HazardType,
  CatalogOption,
} from '@/types/catalog.types';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// Query keys factory
export const catalogKeys = {
  all: ['catalogs'] as const,
  type: (type: CatalogType) => [...catalogKeys.all, type] as const,
  lists: (type: CatalogType) => [...catalogKeys.type(type), 'list'] as const,
  list: (type: CatalogType, includeInactive: boolean) =>
    [...catalogKeys.lists(type), { includeInactive }] as const,
  details: (type: CatalogType) => [...catalogKeys.type(type), 'detail'] as const,
  detail: (type: CatalogType, id: number) => [...catalogKeys.details(type), id] as const,
  dropdown: (type: CatalogType) => [...catalogKeys.type(type), 'dropdown'] as const,
  hierarchy: (type: CatalogType) => [...catalogKeys.type(type), 'hierarchy'] as const,
};

// API endpoint mapping - uses dedicated plural endpoints where available,
// falls back to generic endpoint (singular) for others
const catalogEndpoints: Record<CatalogType, string> = {
  assetClass: 'asset-classes',           // dedicated endpoint
  assetCriticalityFactor: 'asset-criticality-factor', // generic
  assetStatusCatalog: 'asset-status',    // generic
  documentType: 'document-type',         // generic
  problemCode: 'problem-codes',          // dedicated endpoint
  causeCode: 'cause-codes',              // dedicated endpoint
  remedyCode: 'remedy-codes',            // dedicated endpoint
  workOrderPriority: 'work-order-priorities', // dedicated endpoint
  workOrderTypeCatalog: 'work-order-types',   // dedicated endpoint
  laborType: 'labor-type',               // generic
  trade: 'trades',                       // dedicated endpoint
  partCategory: 'part-categories',       // dedicated endpoint
  unitOfMeasure: 'units-of-measure',     // dedicated endpoint
  storageLocationType: 'storage-location-type', // generic
  transactionReason: 'transaction-reason', // generic
  shiftType: 'shift-types',              // dedicated endpoint
  schedulePriority: 'schedule-priority', // generic
  constraintType: 'constraint-type',     // generic
  skill: 'skills',                       // dedicated endpoint
  skillLevel: 'skill-levels',            // dedicated endpoint (via generic)
  certificationType: 'certification-types', // dedicated endpoint
  permitType: 'permit-types',            // dedicated endpoint
  hazardType: 'hazard-types',            // dedicated endpoint
};

// Generic catalog type mapping
type CatalogTypeMap = {
  assetClass: AssetClass;
  assetCriticalityFactor: AssetCriticalityFactor;
  assetStatusCatalog: AssetStatusCatalog;
  documentType: DocumentType;
  problemCode: ProblemCode;
  causeCode: CauseCode;
  remedyCode: RemedyCode;
  workOrderPriority: WorkOrderPriority;
  workOrderTypeCatalog: WorkOrderTypeCatalog;
  laborType: LaborType;
  trade: Trade;
  partCategory: PartCategory;
  unitOfMeasure: UnitOfMeasure;
  storageLocationType: StorageLocationType;
  transactionReason: TransactionReason;
  shiftType: ShiftType;
  schedulePriority: SchedulePriority;
  constraintType: ConstraintType;
  skill: Skill;
  skillLevel: SkillLevel;
  certificationType: CertificationType;
  permitType: PermitType;
  hazardType: HazardType;
};

// ID field mapping
const catalogIdFields: Record<CatalogType, string> = {
  assetClass: 'assetClassId',
  assetCriticalityFactor: 'criticalityFactorId',
  assetStatusCatalog: 'assetStatusCatalogId',
  documentType: 'documentTypeId',
  problemCode: 'problemCodeId',
  causeCode: 'causeCodeId',
  remedyCode: 'remedyCodeId',
  workOrderPriority: 'workOrderPriorityId',
  workOrderTypeCatalog: 'workOrderTypeCatalogId',
  laborType: 'laborTypeId',
  trade: 'tradeId',
  partCategory: 'partCategoryId',
  unitOfMeasure: 'unitOfMeasureId',
  storageLocationType: 'storageLocationTypeId',
  transactionReason: 'transactionReasonId',
  shiftType: 'shiftTypeId',
  schedulePriority: 'schedulePriorityId',
  constraintType: 'constraintTypeId',
  skill: 'skillId',
  skillLevel: 'skillLevelId',
  certificationType: 'certificationTypeId',
  permitType: 'permitTypeId',
  hazardType: 'hazardTypeId',
};

// ============================================================================
// Generic Catalog Hooks
// ============================================================================

/**
 * Get list of catalog items by type
 */
export function useCatalogList<T extends CatalogType>(
  type: T,
  includeInactive = false
) {
  const endpoint = catalogEndpoints[type];

  return useQuery({
    queryKey: catalogKeys.list(type, includeInactive),
    queryFn: async () => {
      const response = await api.get<ApiResponse<CatalogTypeMap[T][]>>(
        `/api/catalogs/${endpoint}`,
        { params: { includeInactive } }
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

/**
 * Get a single catalog item by type and ID
 */
export function useCatalogItem<T extends CatalogType>(
  type: T,
  id: number,
  enabled = true
) {
  const endpoint = catalogEndpoints[type];

  return useQuery({
    queryKey: catalogKeys.detail(type, id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<CatalogTypeMap[T]>>(
        `/api/catalogs/${endpoint}/${id}`
      );
      return response.data.data;
    },
    enabled: enabled && id > 0,
  });
}

/**
 * Get dropdown options for a catalog type
 */
export function useCatalogDropdown(type: CatalogType) {
  const endpoint = catalogEndpoints[type];
  const idField = catalogIdFields[type];

  return useQuery({
    queryKey: catalogKeys.dropdown(type),
    queryFn: async () => {
      const response = await api.get<ApiResponse<Record<string, unknown>[]>>(
        `/api/catalogs/${endpoint}`,
        { params: { includeInactive: false } }
      );
      return response.data.data.map((item) => ({
        value: item[idField] as number,
        label: item.name as string,
        code: item.code as string,
        isActive: item.isActive as boolean,
      })) as CatalogOption[];
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
}

/**
 * Create a new catalog item
 */
export function useCreateCatalog<T extends CatalogType>(type: T) {
  const queryClient = useQueryClient();
  const endpoint = catalogEndpoints[type];

  return useMutation({
    mutationFn: async (data: Partial<CatalogTypeMap[T]>) => {
      const response = await api.post<ApiResponse<CatalogTypeMap[T]>>(
        `/api/catalogs/${endpoint}`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists(type) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.dropdown(type) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.hierarchy(type) });
    },
  });
}

/**
 * Update a catalog item
 */
export function useUpdateCatalog<T extends CatalogType>(type: T) {
  const queryClient = useQueryClient();
  const endpoint = catalogEndpoints[type];

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CatalogTypeMap[T]> }) => {
      const response = await api.put<ApiResponse<CatalogTypeMap[T]>>(
        `/api/catalogs/${endpoint}/${id}`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.detail(type, variables.id) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists(type) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.dropdown(type) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.hierarchy(type) });
    },
  });
}

/**
 * Delete a catalog item
 */
export function useDeleteCatalog(type: CatalogType) {
  const queryClient = useQueryClient();
  const endpoint = catalogEndpoints[type];

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/catalogs/${endpoint}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists(type) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.dropdown(type) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.hierarchy(type) });
    },
  });
}

/**
 * Toggle active status of a catalog item
 */
export function useToggleCatalogStatus(type: CatalogType) {
  const queryClient = useQueryClient();
  const endpoint = catalogEndpoints[type];

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      const response = await api.patch<ApiResponse<unknown>>(
        `/api/catalogs/${endpoint}/${id}/status`,
        { isActive }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.detail(type, variables.id) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists(type) });
      queryClient.invalidateQueries({ queryKey: catalogKeys.dropdown(type) });
    },
  });
}

// ============================================================================
// Specialized Hooks for Hierarchical Catalogs
// ============================================================================

/**
 * Feature 2: bulk-apply an AssetClass to many assets.
 * - scope="ALL" applies to every active asset in the tenant.
 * - scope="LOCATIONS" restricts to the supplied LocationIds[].
 * - scope="ASSETS" restricts to the supplied AssetIds[] (pick specific assets).
 * - overwrite=false (default) skips assets with a different class.
 * Two phases: preview first (no DB change), then commit.
 */
export interface BulkApplyRequest {
  scope: 'ALL' | 'LOCATIONS' | 'ASSETS';
  locationIds?: number[];
  assetIds?: string[];
  overwrite?: boolean;
}

export interface BulkApplyPreview {
  totalMatched: number;
  willUpdate: number;
  willSkip: number;
  conflictsWithClass: number;
}

export function useBulkApplyAssetClassPreview() {
  return useMutation({
    mutationFn: async ({ assetClassId, request }: { assetClassId: number; request: BulkApplyRequest }) => {
      const response = await api.post<ApiResponse<BulkApplyPreview>>(
        `/api/asset-class/${assetClassId}/apply/preview`,
        request
      );
      return response.data.data;
    },
  });
}

export function useBulkApplyAssetClassCommit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetClassId, request }: { assetClassId: number; request: BulkApplyRequest }) => {
      const response = await api.post<ApiResponse<BulkApplyPreview>>(
        `/api/asset-class/${assetClassId}/apply/commit`,
        request
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

/**
 * Get asset classes as hierarchy tree
 */
export function useAssetClassHierarchy() {
  return useQuery({
    queryKey: catalogKeys.hierarchy('assetClass'),
    queryFn: async () => {
      const response = await api.get<ApiResponse<AssetClass[]>>(
        '/api/catalogs/asset-classes/hierarchy'
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Get part categories as hierarchy tree
 */
export function usePartCategoryHierarchy() {
  return useQuery({
    queryKey: catalogKeys.hierarchy('partCategory'),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PartCategory[]>>(
        '/api/catalogs/part-categories/hierarchy'
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

// ============================================================================
// Specialized Hooks for Linked Catalogs
// ============================================================================

/**
 * Get problem codes filtered by asset class
 */
export function useProblemCodesByAssetClass(assetClassId: number | null) {
  return useQuery({
    queryKey: [...catalogKeys.lists('problemCode'), { assetClassId }],
    queryFn: async () => {
      const params = assetClassId ? { assetClassId } : {};
      const response = await api.get<ApiResponse<ProblemCode[]>>(
        '/api/catalogs/problem-codes',
        { params }
      );
      return response.data.data;
    },
    enabled: true,
  });
}

/**
 * Get cause codes filtered by problem code
 */
export function useCauseCodesByProblemCode(problemCodeId: number | null) {
  return useQuery({
    queryKey: [...catalogKeys.lists('causeCode'), { problemCodeId }],
    queryFn: async () => {
      const params = problemCodeId ? { problemCodeId } : {};
      const response = await api.get<ApiResponse<CauseCode[]>>(
        '/api/catalogs/cause-codes',
        { params }
      );
      return response.data.data;
    },
    enabled: true,
  });
}

/**
 * Get remedy codes filtered by cause code
 */
export function useRemedyCodesByCauseCode(causeCodeId: number | null) {
  return useQuery({
    queryKey: [...catalogKeys.lists('remedyCode'), { causeCodeId }],
    queryFn: async () => {
      const params = causeCodeId ? { causeCodeId } : {};
      const response = await api.get<ApiResponse<RemedyCode[]>>(
        '/api/catalogs/remedy-codes',
        { params }
      );
      return response.data.data;
    },
    enabled: true,
  });
}

/**
 * Get skills filtered by trade
 */
export function useSkillsByTrade(tradeId: number | null) {
  return useQuery({
    queryKey: [...catalogKeys.lists('skill'), { tradeId }],
    queryFn: async () => {
      const params = tradeId ? { tradeId } : {};
      const response = await api.get<ApiResponse<Skill[]>>(
        '/api/catalogs/skills',
        { params }
      );
      return response.data.data;
    },
    enabled: true,
  });
}

// ============================================================================
// Convenience Hooks for Specific Catalog Types
// ============================================================================

// Asset Catalogs
export const useAssetClasses = (includeInactive = false) =>
  useCatalogList('assetClass', includeInactive);
export const useAssetClass = (id: number, enabled = true) =>
  useCatalogItem('assetClass', id, enabled);
export const useAssetClassesDropdown = () => useCatalogDropdown('assetClass');

export const useAssetCriticalityFactors = (includeInactive = false) =>
  useCatalogList('assetCriticalityFactor', includeInactive);
export const useAssetCriticalityFactorsDropdown = () =>
  useCatalogDropdown('assetCriticalityFactor');

export const useAssetStatusCatalogs = (includeInactive = false) =>
  useCatalogList('assetStatusCatalog', includeInactive);
export const useAssetStatusCatalogsDropdown = () =>
  useCatalogDropdown('assetStatusCatalog');

export const useDocumentTypes = (includeInactive = false) =>
  useCatalogList('documentType', includeInactive);
export const useDocumentTypesDropdown = () => useCatalogDropdown('documentType');

// Failure Analysis Catalogs
export const useProblemCodes = (includeInactive = false) =>
  useCatalogList('problemCode', includeInactive);
export const useProblemCodesDropdown = () => useCatalogDropdown('problemCode');

export const useCauseCodes = (includeInactive = false) =>
  useCatalogList('causeCode', includeInactive);
export const useCauseCodesDropdown = () => useCatalogDropdown('causeCode');

export const useRemedyCodes = (includeInactive = false) =>
  useCatalogList('remedyCode', includeInactive);
export const useRemedyCodesDropdown = () => useCatalogDropdown('remedyCode');

// Work Order Catalogs
export const useWorkOrderPriorities = (includeInactive = false) =>
  useCatalogList('workOrderPriority', includeInactive);
export const useWorkOrderPrioritiesDropdown = () =>
  useCatalogDropdown('workOrderPriority');

export const useWorkOrderTypeCatalogs = (includeInactive = false) =>
  useCatalogList('workOrderTypeCatalog', includeInactive);
export const useWorkOrderTypeCatalogsDropdown = () =>
  useCatalogDropdown('workOrderTypeCatalog');

// Labor & Trades Catalogs
export const useLaborTypes = (includeInactive = false) =>
  useCatalogList('laborType', includeInactive);
export const useLaborTypesDropdown = () => useCatalogDropdown('laborType');

export const useTrades = (includeInactive = false) =>
  useCatalogList('trade', includeInactive);
export const useTradesDropdown = () => useCatalogDropdown('trade');

// Parts & Inventory Catalogs
export const usePartCategories = (includeInactive = false) =>
  useCatalogList('partCategory', includeInactive);
export const usePartCategoriesDropdown = () => useCatalogDropdown('partCategory');

export const useUnitsOfMeasure = (includeInactive = false) =>
  useCatalogList('unitOfMeasure', includeInactive);
export const useUnitsOfMeasureDropdown = () => useCatalogDropdown('unitOfMeasure');

export const useStorageLocationTypes = (includeInactive = false) =>
  useCatalogList('storageLocationType', includeInactive);
export const useStorageLocationTypesDropdown = () =>
  useCatalogDropdown('storageLocationType');

export const useTransactionReasons = (includeInactive = false) =>
  useCatalogList('transactionReason', includeInactive);
export const useTransactionReasonsDropdown = () =>
  useCatalogDropdown('transactionReason');

// Scheduling Catalogs
export const useShiftTypes = (includeInactive = false) =>
  useCatalogList('shiftType', includeInactive);
export const useShiftTypesDropdown = () => useCatalogDropdown('shiftType');

export const useSchedulePriorities = (includeInactive = false) =>
  useCatalogList('schedulePriority', includeInactive);
export const useSchedulePrioritiesDropdown = () =>
  useCatalogDropdown('schedulePriority');

export const useConstraintTypes = (includeInactive = false) =>
  useCatalogList('constraintType', includeInactive);
export const useConstraintTypesDropdown = () => useCatalogDropdown('constraintType');

// Skills & Certifications Catalogs
export const useSkills = (includeInactive = false) =>
  useCatalogList('skill', includeInactive);
export const useSkillsDropdown = () => useCatalogDropdown('skill');

export const useSkillLevels = (includeInactive = false) =>
  useCatalogList('skillLevel', includeInactive);
export const useSkillLevelsDropdown = () => useCatalogDropdown('skillLevel');

export const useCertificationTypes = (includeInactive = false) =>
  useCatalogList('certificationType', includeInactive);
export const useCertificationTypesDropdown = () =>
  useCatalogDropdown('certificationType');

// Safety Catalogs
export const usePermitTypes = (includeInactive = false) =>
  useCatalogList('permitType', includeInactive);
export const usePermitTypesDropdown = () => useCatalogDropdown('permitType');

export const useHazardTypes = (includeInactive = false) =>
  useCatalogList('hazardType', includeInactive);
export const useHazardTypesDropdown = () => useCatalogDropdown('hazardType');
